import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { ApiContext } from '@api/api.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgWarningExclamation from '@components/ringil3/icons/WarningExclamation.tsx';
import { AuditActionEnum, AuditEntityTypeEnum, AuditEntryOutDTO, AuditFilter, KindEnum } from '@api/ringil-api.v2.tsx';

interface AuditLogHighlightsProps {
  entityId: string;
  actions: AuditActionEnum[];
}

type MessageVariant = 'success' | 'info' | 'error' | 'warning';

const AuditLogHighlights: React.FC<AuditLogHighlightsProps> = ({ entityId, actions }: AuditLogHighlightsProps) => {
  const { queryAuditLog } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [entries, setEntries] = useState<AuditEntryOutDTO[]>([]);

  const variantOf = (sentiment?: string): MessageVariant => {
    switch (sentiment?.toLowerCase()) {
      case 'negative':
        return 'error';
      case 'positive':
        return 'success';
      default:
        return 'info';
    }
  };

  // Sentiment moved into the entry params (kind=SNAPSHOT, name="sentiment") in the 2.0 audit model.
  const sentimentOf = (entry: AuditEntryOutDTO) =>
    (entry.params ?? []).find(p => p.kind === KindEnum.SNAPSHOT && p.name === 'sentiment')?.value;

  // Free text first, else the translatable descriptionKey + its DESCRIPTION params (name or position).
  const descriptionOf = (entry: AuditEntryOutDTO) => {
    if (entry.description) return entry.description;
    if (!entry.descriptionKey) return '';
    const values: Record<string, string> = {};
    (entry.params ?? [])
      .filter(p => p.kind === KindEnum.DESCRIPTION)
      .forEach(p => {
        const v = p.value ?? '—';
        values[String(p.position)] = v;
        if (p.name) values[p.name] = v;
      });
    return tr(entry.descriptionKey, entry.descriptionKey, values);
  };

  // Callers pass a fresh array literal each render; depend on a stable key so we fetch once per change.
  const actionsKey = actions.join(',');
  useEffect(() => {
    if (!actions.length) return;
    const filter: AuditFilter = {
      entityType: AuditEntityTypeEnum.TIMESLOT as unknown as AuditFilter['entityType'],
      entityId,
      action: actions as unknown as AuditFilter['action'],
    };
    // Rows come back newest-first; keep the first entry per action = last-update-per-type.
    queryAuditLog({ pageNumber: 1, pageSize: 100 }, filter, data => {
      const seen = new Set<string>();
      const latestPerAction = (data?.data ?? []).filter(e => {
        if (seen.has(e.auditAction)) return false;
        seen.add(e.auditAction);
        return true;
      });
      setEntries(latestPerAction);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, actionsKey]);

  return (
    <Container>
      {entries.map(entry => (
        <LogWrapper key={entry.auditAction}>
          <COInlineMessage variant={variantOf(sentimentOf(entry))} icon={<SvgWarningExclamation />}>
            <LogContent>
              <Message>{descriptionOf(entry)}</Message>
            </LogContent>
          </COInlineMessage>
        </LogWrapper>
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LogWrapper = styled.div`
  width: 100%;
`;

const LogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--content-primary);
`;

export default AuditLogHighlights;
